import React, { useState, Dispatch, SetStateAction } from 'react';
import styled from '@emotion/styled';
import { Field, FormSection, RequiredAsterisk, FormHeader } from '@atlaskit/form';
import { Box, Text, Flex, xcss } from '@atlaskit/primitives';
import { RadioGroup } from '@atlaskit/radio';
import { OptionsPropType } from '@atlaskit/radio/types';
import SectionMessage from '@atlaskit/section-message';
import { AddContactMultiSelectField } from '@css-xp/ppc-add-contact-fields';
import { PaymentMethodStep } from '../../components/billing-profile-components';
import { BillingAdmin } from '@css-xp/ppc-graphtypes';
import { getBillingProfileUrl } from '@css-xp/ppc-common';

export const PartnerDetailSectionStyles = styled.div`
  min-height: 380px;
  max-height: 380px;
  overflow-y: auto;
  margin-right: -15px;
  padding-right: 15px;
  margin-top: 12px;
`;

export const BillingAdminSectionStyles = styled.div`
  > div {
    > div > div {
      margin-top: 0px;
    }
  }
`;

enum SubscriptionBillingFrequency {
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
  BOTH = 'Both',
}
const subscriptionItems: OptionsPropType = [
  { name: 'subscription', value: SubscriptionBillingFrequency.MONTHLY, label: 'Monthly' },
  { name: 'subscription', value: SubscriptionBillingFrequency.ANNUAL, label: 'Annual' },
];
const warningMessageStyles = xcss({
  paddingTop: 'space.200',
});

interface PartnerDetailsStepProps {
  onFieldValueChange: (name: string, value: any) => void;
  availablePaymentMethods: Array<any>;
  selectedPaymentId?: string;
  termPaymentStatus?: string;
  isMultiCurrencyChangesEnabled?: boolean;
  billingAdmins: Array<BillingAdmin>;
  setBillingAdmins: Dispatch<SetStateAction<Array<BillingAdmin>>>;
  txaBillingAdmins: Array<any>;
  billingAdminsLoading: boolean;
  existingBillingProfileDetails?: any;
  billingProfileData: any;
  transactionAccountId: string;
  showMonthlyDefaultSelected?: boolean;
  shouldHideFormHeader?: boolean;
  shouldRenderForInternalUse?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

export const PartnerDetailsStep = ({
  availablePaymentMethods,
  billingProfileData,
  onFieldValueChange,
  selectedPaymentId,
  termPaymentStatus,
  isMultiCurrencyChangesEnabled,
  billingAdmins,
  setBillingAdmins,
  txaBillingAdmins,
  billingAdminsLoading,
  existingBillingProfileDetails,
  transactionAccountId,
  showMonthlyDefaultSelected,
  shouldHideFormHeader,
  shouldRenderForInternalUse,
  shouldShowACHPaymentMethod,
}: PartnerDetailsStepProps) => {
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionBillingFrequency | undefined>(
    billingProfileData?.subscriptions ?? shouldRenderForInternalUse ? SubscriptionBillingFrequency.ANNUAL : undefined
  );
  const selectedBillingAdmins = billingProfileData?.['billing-contact-select'];
  const [showExistingBpWarning, setShowExistingBpWarning] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(selectedPaymentId);
  const [billingProfileDetails, setBillingProfileDetails] = useState<{ name: string; id: string }[]>([]);
  React.useEffect(() => {
    if (existingBillingProfileDetails) {
      setShowExistingBpWarning(false);
      const soldToAddressId = billingProfileData?.soldToAddress?.id;
      const billingProfileNodes = existingBillingProfileDetails?.filter((billingProfile: any) => {
        if (billingProfile?.defaultPaymentMethod?.id) {
          return (
            billingProfile?.defaultPaymentMethod?.id === selectedPaymentMethodId &&
            billingProfile?.shipToParty?.id === soldToAddressId
          );
        } else {
          return false;
        }
      });
      if (billingProfileNodes?.length > 0) {
        const billingProfiles = billingProfileNodes.map((node: any) => ({
          name: node.name,
          id: node.id,
        }));
        setBillingProfileDetails(billingProfiles);
        setShowExistingBpWarning(true);
      }
    }
  }, [billingProfileData, existingBillingProfileDetails, selectedPaymentMethodId]);

  //@ts-ignore
  const existingBillingAdminIds = billingAdmins?.map(({ accountId }) => accountId);
  const getAvailablePaymentMethods = (selectedSubscriptionType?: SubscriptionBillingFrequency) => {
    if (selectedSubscriptionType === SubscriptionBillingFrequency.ANNUAL) {
      return availablePaymentMethods;
    }
    return availablePaymentMethods?.filter((paymentMethod) => paymentMethod?.type !== 'DEFERRED');
  };

  const renderFormHeader = () => {
    if (shouldHideFormHeader) {
      return null;
    }

    if (shouldRenderForInternalUse) {
      return (
        <FormHeader>
          <h3>2. Payment method</h3>
        </FormHeader>
      );
    }

    return (
      <FormHeader>
        <h3>Add partner details</h3>
        Required fields are marked with an asterisk <RequiredAsterisk />
      </FormHeader>
    );
  };

  const getDefaultValueForSubscriptionField = () => {
    if (shouldRenderForInternalUse) {
      // Annual to be selected by default for IUL
      return subscriptionItems[1]?.value;
    }

    return showMonthlyDefaultSelected ? subscriptionItems[0]?.value : undefined;
  };

  return (
    <FormSection>
      <Box id="partner-details-step">
        {renderFormHeader()}
        <PartnerDetailSectionStyles>
          <Field
            label="What subscriptions will be added?"
            name="subscriptions"
            defaultValue={billingProfileData?.subscriptions}
            isRequired
          >
            {({ fieldProps }) => (
              <RadioGroup
                {...fieldProps}
                // Render only Annual for IUL
                options={shouldRenderForInternalUse ? [subscriptionItems[1]] : subscriptionItems}
                isDisabled={showMonthlyDefaultSelected}
                onChange={(event) => {
                  fieldProps.onChange(event);
                  setSelectedSubscriptionType(event.currentTarget.value as SubscriptionBillingFrequency);
                }}
                defaultValue={getDefaultValueForSubscriptionField()}
              />
            )}
          </Field>
          {(showMonthlyDefaultSelected ||
            selectedSubscriptionType ||
            billingProfileData?.subscriptions ||
            selectedPaymentId) && (
            <Box>
              <PaymentMethodStep
                availablePaymentMethods={getAvailablePaymentMethods(
                  billingProfileData?.subcriptions ?? selectedSubscriptionType
                )}
                onFieldValueChange={onFieldValueChange}
                setPaymentMethodId={setSelectedPaymentMethodId}
                selectedPaymentId={selectedPaymentId}
                termPaymentStatus={termPaymentStatus}
                isPaypalSupportEnabled={true}
                isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
                hideWarningMessage={true}
                headingMessage={'Select a payment method for the billing profile'}
                version={2}
                showHeader={true}
                shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
              />
            </Box>
          )}
          <Flex xcss={warningMessageStyles}>
            {showExistingBpWarning && (
              <SectionMessage title="Does this billing profile already exist?">
                {' '}
                <Text>
                  We found at least one billing profile with the same sold-to address and payment method. Review to see
                  if there's a match for the one you're after:{' '}
                  {billingProfileDetails?.length > 0 &&
                    billingProfileDetails.map((billingProfile, index) => {
                      return (
                        <div key={index}>
                          {getBillingProfileUrl(billingProfile?.name, billingProfile?.id, transactionAccountId)}
                        </div>
                      );
                    })}
                </Text>{' '}
              </SectionMessage>
            )}
          </Flex>
          {shouldRenderForInternalUse || (
            <Box id="billing-admin-selection" xcss={xcss({ paddingBottom: 'space.400' })}>
              <BillingAdminSectionStyles>
                <Field label="Enter a billing admin (optional)" name="billing-admin-contact">
                  {() => (
                    <AddContactMultiSelectField
                      currentUserList={existingBillingAdminIds}
                      emailFieldText="Select from the list"
                      fieldType="billing-contact"
                      billingAdmins={txaBillingAdmins.map((admin) => admin.node)}
                      billingAdminsLoading={billingAdminsLoading}
                      setBillingAdmins={setBillingAdmins}
                      selectedUserList={selectedBillingAdmins ?? []}
                    />
                  )}
                </Field>
              </BillingAdminSectionStyles>
            </Box>
          )}
        </PartnerDetailSectionStyles>
      </Box>
    </FormSection>
  );
};
